import React from 'react'
import styled from 'styled-components'
import { Icon } from '@musinsa/pbo-assets'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { DatePickerValue } from '@/components/content/DatePicker/DatePickerInput'
import { DatePickerInputV2 } from '@/components/content/DatePicker/DatePickerInputV2'

export interface PeriodFieldProps {
  label: string
  date: DatePickerValue
  time: string
  onDateChange: (v: DatePickerValue) => void
  onTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  errorText?: string
  datePlaceholder?: string
  timePlaceholder?: string
  minDate?: Date
  maxDate?: Date
}

export function PeriodField({
  label,
  date,
  time,
  onDateChange,
  onTimeChange,
  errorText,
  datePlaceholder = '날짜',
  timePlaceholder = '시간',
  minDate,
  maxDate,
}: PeriodFieldProps) {
  return (
    <Wrap>
      <Box $isError={!!errorText}>
        <BoxLabel>{label}</BoxLabel>
        <BoxInner>
          <DateSection hasValue={!!date}>
            <Icon name="ic-calendar" size={16} color={tokens['fg-lower']} />
            <DateInputWrap>
              {!date && <DatePlaceholder>{datePlaceholder}</DatePlaceholder>}
              <DatePickerInputV2
                value={date}
                onChange={onDateChange}
                onReset={() => onDateChange(null)}
                showTimeSelect={false}
                fixedCalendar
                yearPlaceholder=""
                monthPlaceholder=""
                dayPlaceholder=""
                calendarIcon={null}
                minDate={minDate}
                maxDate={maxDate}
              />
            </DateInputWrap>
          </DateSection>
          <Divider />
          <TimeSection>
            <Icon name="ic-clockCircle" size={16} color={tokens['fg-lower']} />
            <TimeInput value={time} onChange={onTimeChange} placeholder={timePlaceholder} maxLength={5} />
          </TimeSection>
        </BoxInner>
      </Box>
      {errorText && <ErrorText>{errorText}</ErrorText>}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`

const Divider = styled.div`
  width: 1px;
  height: 100%;
  background: var(--stroke-divider-low, rgba(138, 138, 138, 0.2));
  flex-shrink: 0;
`

const Box = styled.div<{ $isError: boolean }>`
  position: relative;
  border: 1px solid ${({ $isError }) => ($isError ? 'var(--stroke-border-critical, #f31110)' : 'var(--select-border, #e0e0e0)')};
  border-radius: 4px;

  & ${Divider} {
    background: ${({ $isError }) => ($isError ? 'var(--stroke-border-critical, #f31110)' : 'var(--select-border, #e0e0e0)')};
  }
`

const BoxLabel = styled.span`
  position: absolute;
  top: -10px;
  left: 12px;
  padding: 0 4px;
  background: ${tokens.bg};
  font-size: 11px;
  line-height: 18px;
  color: var(--fg-lower, #999999);
  z-index: 1;
`

const BoxInner = styled.div`
  display: flex;
  align-items: stretch;
  height: 44px;
`

const DateSection = styled.div<{ hasValue: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .react-datetime-picker {
    width: 100%;
    height: 44px;
  }
  .react-datetime-picker__wrapper {
    width: 100%;
    height: 100%;
    border: none;
    align-items: center;
  }

  ${({ hasValue }) =>
    !hasValue &&
    `.react-datetime-picker__inputGroup__divider {
      color: transparent;
    }`}
`

const DateInputWrap = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`

const DatePlaceholder = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  color: var(--fg-lower, #999999);
  font-size: 14px;
  line-height: 1;
  pointer-events: none;
  z-index: 1;
`

const TimeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  flex-shrink: 0;
  width: 116px;
`

const TimeInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--fg, #1a1a1a);

  &::placeholder {
    color: var(--fg-lower, #999999);
  }
`

const ErrorText = styled.p`
  font-size: 14px;
  line-height: 1.4;
  color: var(--fg-critical, #f31110);
`
